import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";
import DatePicker from "~/molecules/DatePicker";
import { Textarea } from "~/atoms/textarea";
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import { type WorkflowEditorFormValues } from "../workflowEditorForm";
import { type WorkflowEditorMode } from "~/pages/workflow-editor/useWorkflowEditorPage";

interface Props {
  mode: WorkflowEditorMode;
}

export function WorkflowMetadataSection({ mode }: Props) {
  const { t } = useTranslation();
  const form = useFormContext<WorkflowEditorFormValues>();

  const isPreviewMode = mode.type === "previewing";
  const workflowHasStarted = mode.workflow.stepInstances.length > 0;
  const workflowHasEnded = mode.workflow.documentVersion.publishedAt !== null;

  const reviewFromMaxDate = useWatch({
    control: form.control,
    name: "reviewTo",
  });

  const reviewToMinDate = useWatch({
    control: form.control,
    name: workflowHasStarted ? "actualStartDate" : "reviewFrom",
  });

  const createdFromTemplateLabel =
    mode.workflow.workflowTemplateId != null
      ? t(($) => $.commons.workflow.createdFromTemplate, {
          name:
            mode.workflow.workflowTemplateName ??
            t(($) => $.workflowTemplates.defaultBadge),
        })
      : undefined;

  return (
    <>
      {createdFromTemplateLabel && (
        <div className="flex justify-end">
          <p className="text-muted-foreground mt-1 text-sm">
            {createdFromTemplateLabel}
          </p>
        </div>
      )}
      <div className="grid gap-8 xl:grid-cols-2">
        <div>
          <FormField
            control={form.control}
            name="reviewFrom"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t(($) => $.commons.document.reviewFrom)}</FormLabel>
                <FormDescription>
                  {t(($) => $.commons.document.reviewFromDescription)}
                </FormDescription>
                <DatePicker
                  value={field.value}
                  onChange={(date) => field.onChange(date ?? null)}
                  minDate={new Date()}
                  maxDate={reviewFromMaxDate ?? undefined}
                  disabled={isPreviewMode || workflowHasStarted}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="reviewTo"
            render={({ field }) => (
              <FormItem className="pt-4">
                <FormLabel>{t(($) => $.commons.document.reviewTo)}</FormLabel>
                <FormDescription>
                  {t(($) => $.commons.document.reviewToDescription)}
                </FormDescription>
                <DatePicker
                  value={field.value}
                  onChange={(date) => field.onChange(date ?? null)}
                  minDate={reviewToMinDate ?? new Date()}
                  disabled={isPreviewMode || workflowHasEnded}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t(($) => $.commons.workflow.description)}</FormLabel>
              <FormDescription>
                {t(($) => $.commons.workflow.descriptionDescription)}
              </FormDescription>
              <Textarea
                value={isPreviewMode ? mode.description : field.value}
                onChange={field.onChange}
                className="h-40"
                disabled={isPreviewMode}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
}
